import { useEffect, useId, useRef, useState } from 'react'
import caseDrops from './productDropScenes.json'
import phoneDrops from './phoneProductDropScenes.json'
import { storefrontImage } from '@/lib/storefrontImage'

const drops = {
  '/product-demos/dope-swag-vape-case.mp4': {
    key: 'dope-mint',
    case: { x: 329, y: 325, width: 174, height: 526.0465 },
    insert: { x: 349.907, y: 190.1163, width: 132.186, height: 702.0698 },
    rim: 337.173, bottom: 829.92, distance: 183.1163,
  },
  '/product-demos/sick-cricket-lighter.mp4': {
    key: 'sick-lighter',
    case: { x: 314, y: 300, width: 204, height: 545.36 },
    insert: { x: 335.76, y: 211.6, width: 160.48, height: 596.36 },
    // This photograph has an opaque lip, matching the original foreground layer.
    rim: 300, bottom: 822, distance: 105.4,
  },
} as const

export interface ProductDropScene {
  key: string
  canvasHeight?: number
  case: { x: number; y: number; width: number; height: number }
  insert: { x: number; y: number; width: number; height: number }
  rim: number
  bottom: number
  distance: number
}

/** Match the existing stable media path, including when the product name is translated. */
export function productDropFor(demoVideoUrl: string | null, cutoutUrl: string | null = null) {
  const caseDrop = caseDrops[cutoutUrl as keyof typeof caseDrops]
  if (caseDrop) return caseDrop
  return drops[demoVideoUrl as keyof typeof drops] ?? null
}

/** Phone hero shots use the featured product's approved insertion framing. */
export function phoneProductDropFor(cutoutUrl: string | null): ProductDropScene | null {
  return phoneDrops[cutoutUrl as keyof typeof phoneDrops] ?? null
}

/** Photographic product motion: one short compositor animation, then a completely idle image. */
interface ProductDropProps {
  scene: ProductDropScene
  alt: string
  static?: boolean
  active?: boolean
  className?: string
  onSettled?: () => void
}

/** Inactive scenes must have neither SVG image hrefs nor an image preloader. */
export function ProductDrop({ active = true, ...props }: ProductDropProps) {
  return active ? <ProductDropContent {...props} /> : null
}

function ProductDropContent({ scene, alt, static: still = false, className = '', onSettled }: ProductDropProps) {
  const id = useId().replace(/:/g, '')
  const rig = useRef<SVGGElement>(null)
  const insert = useRef<SVGImageElement>(null)
  const [state, setState] = useState('loading')
  const base = `/product-motion/${scene.key}`
  // Static public media is loaded from the manifest's optimized AVIF when it exists.
  const backgroundSrc = storefrontImage('/product-motion/background.webp')
  const caseSrc = storefrontImage(`${base}/case.webp`)
  const insertSrc = storefrontImage(`${base}/insert.webp`)
  const canvasHeight = scene.canvasHeight ?? 1040
  const center = scene.case.x + scene.case.width / 2
  const ground = scene.case.y + scene.case.height

  useEffect(() => {
    let cancelled = false
    let ready = false
    let started = false
    let animations: Animation[] = []
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    const finish = (notify = true) => {
      started = true
      animations.forEach(animation => animation.cancel())
      animations = []
      if (insert.current) insert.current.style.transform = ''
      if (!cancelled) setState('settled')
      if (notify && !cancelled && !still) onSettled?.()
    }
    const change = () => { if (motion.matches) finish() }
    motion.addEventListener('change', change)
    if (insert.current && !still && !motion.matches) {
      insert.current.style.transform = `translateY(-${scene.distance}px)`
    }

    const start = async () => {
      if (cancelled || !ready || started || !insert.current || !rig.current) return
      if (still || motion.matches || !Element.prototype.animate) { finish(); return }
      started = true
      insert.current.style.transform = ''
      setState('playing')
      const drop = insert.current.animate([
        { transform: `translateY(-${scene.distance}px)`, offset: 0, easing: 'cubic-bezier(.333333,0,.666667,0)' },
        { transform: `translateY(-${scene.distance / 2}px)`, offset: .5, easing: 'cubic-bezier(.333333,1,.666667,1)' },
        { transform: 'translateY(0)', offset: 1 },
      ], { delay: 240, duration: 1720, fill: 'both' })
      const smooth = (value: number) => { const v = Math.max(0, Math.min(1, value)); return v * v * (3 - 2 * v) }
      const frames = Array.from({ length: 172 }, (_, i) => {
        const t = i / 171 * 2.85
        const envelope = smooth(t / .28) * (1 - smooth((t - 2.10) / .75))
        const amount = .25 * envelope * (.65 + .55 * Math.exp(-Math.pow((t - 1.96) / .18, 2)))
        const x = amount * (1.5 * Math.sin(10.7 * t + .6) + .65 * Math.sin(27 * t + 1.7))
        const y = amount * (Math.sin(7.5 * t + 2) + .45 * Math.sin(32.7 * t + .2))
        return { offset: i / 171, transform: `translate(${x}px, ${y}px) rotate(${amount * .035 * Math.sin(13.8 * t + .4)}deg)` }
      })
      animations = [drop, rig.current.animate(frames, { duration: 2850, fill: 'both' })]
      try { await Promise.all(animations.map(animation => animation.finished)); finish() }
      catch { /* Unmount or a changed motion preference cancels playback. */ }
    }
    const images = [caseSrc, insertSrc, backgroundSrc]
    Promise.all(images.map(src => {
      const image = new Image()
      image.src = src
      return image.decode()
    })).then(() => {
      if (cancelled) return
      ready = true
      if (!started) setState('ready')
      void start()
    }).catch(() => {
      finish(false)
      if (!cancelled) setState('failed')
    })

    return () => {
      cancelled = true
      animations.forEach(animation => animation.cancel())
      motion.removeEventListener('change', change)
    }
  }, [base, caseSrc, insertSrc, backgroundSrc, scene, still])

  return (
    <svg
      viewBox={`0 0 832 ${canvasHeight}`}
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={alt}
      data-product-drop={scene.key}
      data-state={state}
      className={`block size-full overflow-hidden bg-black ${className}`}
    >
      <defs>
        <clipPath id={`${id}-back`}><rect width="832" height={scene.rim} /></clipPath>
        <clipPath id={`${id}-front`}><rect y={scene.rim} width="832" height={canvasHeight - scene.rim} /></clipPath>
        <clipPath id={`${id}-insert`}><rect width="832" height={scene.bottom} /></clipPath>
        <radialGradient id={`${id}-shadow`}><stop stopColor="#000" stopOpacity=".65" /><stop offset="1" stopColor="#000" stopOpacity="0" /></radialGradient>
      </defs>
      <image href={backgroundSrc} width="832" height={canvasHeight} preserveAspectRatio="xMidYMid slice" />
      <ellipse cx={center} cy={ground + 5} rx={scene.case.width * .6} ry="18" fill={`url(#${id}-shadow)`} />
      <g ref={rig} style={{ transformOrigin: `${center}px ${scene.case.y + scene.case.height / 2}px` }}>
        <image data-drop-case="" href={caseSrc} {...scene.case} clipPath={`url(#${id}-back)`} />
        <g clipPath={`url(#${id}-insert)`}>
          <image ref={insert} data-drop-insert="" href={insertSrc} {...scene.insert} />
        </g>
        <image data-drop-case="" href={caseSrc} {...scene.case} clipPath={`url(#${id}-front)`} />
      </g>
    </svg>
  )
}
